import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import _debounce from "lodash/debounce";
import {
  clearFacilityFilter,
  doFilter as doFacilityFilter,
  filterWellsFromMapExtent as filterFacilityFromMapExtent
} from "store/features/filter/facilityFilterSlice";
import {
  clearWellFilter,
  doFilter,
  filterWellsFromMapExtent
} from "store/features/filter/filterSlice";
import { polygonCoordsEqual } from "store/features/filter/utils/polygonCoordsEqual";
import { RootState } from "store/rootReducer";
import { useFilterScheduledStore } from "stores/filterScheduledStore";
import {
  selectIsMapMoving,
  selectMapExtent,
  useClearFacilityMap,
  useClearWellMap,
  useMapStore
} from "stores/map";
import {
  selectMapViewFollowUpOwed,
  useMapViewFollowUpStore
} from "stores/mapViewFollowUpStore";
import { useWellListOpenStore } from "stores/wellListOpenStore";

import { useUserSettings } from "components/user/hooks";

import { EntityKind } from "../../models/entityKind";
import useFilterQuery from "./hooks/useFilterQuery";

export default function useFilterWatcher(
  isPopoutDashboard: boolean,
  entityKind: EntityKind
) {
  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);
  const clearWellMap = useClearWellMap();
  const clearFacilityMap = useClearFacilityMap();
  function getFilterSelector(entityKind: EntityKind, state: RootState, name: string) {
    const reducer = entityKind === EntityKind.Well ? state.filter : state.facilityFilter;
    return reducer[name];
  }

  const dispatch = useDispatch();

  const { zoomToFilterSetting } = useUserSettings();
  const isZoomToFilterEnabled = zoomToFilterSetting?.isZoomToFilterEnabled;
  // Read via ref at dispatch time so toggling this setting never recreates
  // debouncedFilter below and re-triggers a filter round trip with no actual
  // filter change.
  const zoomToFilterEnabledRef = useRef(isZoomToFilterEnabled);
  zoomToFilterEnabledRef.current = isZoomToFilterEnabled;

  // Marks a polygonFilter update as coming from the viewport-extent sync
  // effect below (panning/zooming the map) rather than a user filter action.
  // Set right before that effect dispatches filterWellsFromMapExtent, and
  // consumed by the main watcher effect so it never counts toward zooming.
  const extentSyncRef = useRef(false);

  // Whether a user filter change happened since debouncedFilter last ran.
  // OR-accumulated because several effect firings can land inside a single
  // debounce window before doFilter actually fires.
  const zoomEligibleRef = useRef(false);

  // Whether a follow-up run is waiting out the debounce window. The owed flag
  // is cleared as that run is scheduled, so it cannot answer this on its own.
  const followUpPendingRef = useRef(false);

  // state from store
  const excludePolygonsFilter = useSelector(
    (state: RootState) => state.filter.excludePolygonsFilter
  );
  const mapExtent = useMapStore(selectMapExtent);
  const isMapMoving = useMapStore(selectIsMapMoving);
  const polygonFilter = useSelector((state: RootState) =>
    getFilterSelector(entityKind, state, "polygonFilter")
  );
  const mapViewFilterDisabled = useSelector((state: RootState) =>
    getFilterSelector(entityKind, state, "mapViewFilterDisabled")
  );
  // Read inside the extent-sync effects below without being one of their
  // dependencies. Those effects answer "the viewport moved"; a change to this
  // flag on its own is not a move, and re-running them on it would restate the
  // viewport over whatever polygon just cleared the flag.
  const mapViewFilterDisabledRef = useRef(mapViewFilterDisabled);
  mapViewFilterDisabledRef.current = mapViewFilterDisabled;
  const followUpOwedSelector = useMemo(
    () => selectMapViewFollowUpOwed(entityKind),
    [entityKind]
  );
  const mapViewFollowUpOwed = useMapViewFollowUpStore(followUpOwedSelector);
  // Latest polygonFilter, read inside the extent-sync effect below without
  // adding polygonFilter to that effect's deps (which would re-run the sync
  // on every polygon change). Used with polygonCoordsEqual to run the reducer's
  // no-op check before arming the extent-sync flag.
  const latestPolygonFilterRef = useRef(polygonFilter);
  latestPolygonFilterRef.current = polygonFilter;
  const propertiesFilter = useSelector((state: RootState) =>
    getFilterSelector(entityKind, state, "propertiesFilter")
  );
  const wellListFilter = useSelector((state: RootState) =>
    getFilterSelector(entityKind, state, "wellListFilter")
  );
  const filtersLoaded = useSelector((state: RootState) =>
    getFilterSelector(entityKind, state, "filtersLoaded")
  );
  const checkedPadScenarios = useSelector(
    (state: RootState) => state.app.checkedPadScenarios
  );
  // Stable key derived from scenario IDs only — sticks arrays update asynchronously
  // and must not re-trigger the filter when no scenario was added or removed.
  const checkedScenarioIds = useMemo(
    () => checkedPadScenarios.map((s) => s.scenarioId).join(","),
    [checkedPadScenarios]
  );

  // Previous values of the main watcher effect's filter deps, used to tell a
  // real user filter change apart from a dependency-identity churn (e.g.
  // debouncedFilter being recreated). Initialized to the current values so
  // the first render is a no-change run instead of a false "everything
  // changed" on mount.
  const prevPolygonFilterRef = useRef(polygonFilter);
  // Same idea for the suppression flag, whose transition runs a filter of its
  // own below. Initialized to the current value so mounting with the map view
  // already removed is not read as the user having just removed it.
  const previousMapViewFilterDisabledRef = useRef(mapViewFilterDisabled);
  // The polygon the viewport stopped feeding when the map view came out. The
  // restate below is for that polygon and nothing else, so it is compared by
  // identity. Carrying no source cannot answer this on its own: a saved filter
  // written before lassos were marked holds an unmarked lasso, and restoring
  // one is what clears the flag. Initialized to the current polygon so mounting
  // with the map view already removed still has the frozen one on hand.
  const frozenPolygonRef = useRef(polygonFilter);
  const prevPropertiesFilterRef = useRef(propertiesFilter);
  const prevWellListFilterRef = useRef(wellListFilter);
  const prevExcludePolygonsFilterRef = useRef(excludePolygonsFilter);
  const prevCheckedScenarioIdsRef = useRef(checkedScenarioIds);

  const { getFilterFromQueryScreenshot } = useFilterQuery();

  const isWellActive = useMemo(
    () => activeEntities.includes(EntityKind.Well),
    [activeEntities]
  );
  const isFacilityActive = useMemo(
    () => activeEntities.includes(EntityKind.Facility),
    [activeEntities]
  );

  const setFollowUpOwed = useCallback(
    (owed: boolean) => {
      useMapViewFollowUpStore.getState().setMapViewFollowUpOwed(entityKind, owed);
    },
    [entityKind]
  );

  // The debounce instance that last scheduled a run. debouncedFilter is rebuilt
  // when activeEntities change, and the old instance's pending run still fires.
  const latestScheduledRunRef = useRef<Maybe<() => void>>(undefined);

  const debouncedFilter = useMemo(() => {
    const setScheduled = (scheduled: boolean) =>
      useFilterScheduledStore.getState().setFilterScheduled(entityKind, scheduled);
    const run = _debounce(() => {
      // Whether a real user filter change (not a viewport-extent sync)
      // happened since this last fired. The filter thunks need it on its own
      // as well: a viewport-driven refilter must not restate the zoom extent
      // even when the zoom setting is off.
      const isUserFilterChange = zoomEligibleRef.current;
      // Only zoom if the setting is on AND the change was the user's.
      const shouldZoomToFilter = zoomToFilterEnabledRef.current && isUserFilterChange;
      zoomEligibleRef.current = false;
      // Whatever this run was scheduled for, it is running now, so there is
      // no scheduled run left for the camera to take back.
      followUpPendingRef.current = false;
      if (
        entityKind === EntityKind.Facility &&
        activeEntities.includes(EntityKind.Facility)
      ) {
        dispatch(
          doFacilityFilter(
            getFilterFromQueryScreenshot,
            shouldZoomToFilter,
            isUserFilterChange
          )
        );
      }
      if (entityKind === EntityKind.Well && activeEntities.includes(EntityKind.Well)) {
        dispatch(
          doFilter(getFilterFromQueryScreenshot, shouldZoomToFilter, isUserFilterChange)
        );
      }
      // Cleared after the dispatch, which raises isFiltering before its first
      // await, so readers never see a gap between the two flags. A superseded
      // instance still fires, and must not clear a newer instance's change.
      if (latestScheduledRunRef.current === run) setScheduled(false);
    }, 800);
    return Object.assign(
      () => {
        latestScheduledRunRef.current = run;
        setScheduled(true);
        run();
      },
      {
        cancel: () => {
          run.cancel();
          if (latestScheduledRunRef.current === run) setScheduled(false);
        }
      }
    );
  }, [dispatch, activeEntities, entityKind]);

  useEffect(() => {
    if (!mapExtent.coordinates.length) return;

    // The map view removed from the filter means the viewport stops feeding
    // it. Read through the ref, never as a dependency: see the note on the
    // ref itself.
    if (
      entityKind === EntityKind.Facility &&
      isFacilityActive &&
      !mapViewFilterDisabledRef.current
    ) {
      // Same reasoning as the well extent-sync effect below: flag this
      // polygonFilter update as extent-driven so the main watcher doesn't
      // treat a pan as a user filter change and re-zoom to it (the facility
      // pan-zoom loop). Only arm the flag when the update will actually land:
      // filterWellsFromMapExtent no-ops when the coordinates are unchanged, and
      // arming on a no-op would strand the flag true and suppress the next real
      // user polygon change from zooming.
      if (!polygonCoordsEqual(latestPolygonFilterRef.current, mapExtent)) {
        extentSyncRef.current = true;
      }
      dispatch(filterFacilityFromMapExtent(mapExtent));
    }

    if (!isFacilityActive) {
      clearFacilityMap();
      dispatch(clearFacilityFilter());
    }
  }, [dispatch, mapExtent, isFacilityActive]);

  useEffect(() => {
    if (!mapExtent.coordinates.length) return;

    if (
      entityKind === EntityKind.Well &&
      isWellActive &&
      !mapViewFilterDisabledRef.current
    ) {
      // Flag this polygonFilter update as extent-driven before it lands, so
      // the main watcher effect below doesn't treat it as a user filter change
      // and re-zoom to it. Only arm the flag when the update will actually
      // land: filterWellsFromMapExtent no-ops when the coordinates are
      // unchanged, which leaves no polygonFilter change for the main effect to
      // clear the flag on. Arming on a no-op would strand it true and suppress
      // the next real user polygon change from zooming.
      if (!polygonCoordsEqual(latestPolygonFilterRef.current, mapExtent)) {
        extentSyncRef.current = true;
      }
      dispatch(filterWellsFromMapExtent(mapExtent));
    }

    if (!isWellActive) {
      clearWellMap();
      dispatch(clearWellFilter());
    }
  }, [dispatch, isWellActive, mapExtent]);

  useEffect(() => {
    // Not exactly sure of the root cause, but the filters do not seem to persist between popout dashboards
    // Likely in StandaloneDashboard.tsx, propertiesFilter need to be passed through the channel so they can be used here.
    // Right now only filter id is passed.

    // This seems acceptable, unless popout dashboards can have a map in the future, or affect the selectors above

    const polygonChanged = polygonFilter !== prevPolygonFilterRef.current;
    const propertiesChanged = propertiesFilter !== prevPropertiesFilterRef.current;
    const wellListChanged = wellListFilter !== prevWellListFilterRef.current;
    const excludePolygonsChanged =
      excludePolygonsFilter !== prevExcludePolygonsFilterRef.current;
    const scenariosChanged = checkedScenarioIds !== prevCheckedScenarioIdsRef.current;

    // A polygonFilter change only counts as a user filter action if it isn't
    // coming from the viewport-extent sync effect above. Without this check
    // every pan would be eligible to zoom, which moves the viewport, which
    // changes the extent, which zooms again — an endless zoom-out loop.
    const userInitiatedPolygonChange = polygonChanged && !extentSyncRef.current;
    if (polygonChanged) {
      extentSyncRef.current = false;
    }

    if (
      propertiesChanged ||
      wellListChanged ||
      excludePolygonsChanged ||
      scenariosChanged ||
      userInitiatedPolygonChange
    ) {
      zoomEligibleRef.current = true;
      // The one-well open in flight answers the filter this change replaces.
      if (entityKind === EntityKind.Well) {
        useWellListOpenStore.getState().setPending(undefined);
      }
    }

    prevPolygonFilterRef.current = polygonFilter;
    prevPropertiesFilterRef.current = propertiesFilter;
    prevWellListFilterRef.current = wellListFilter;
    prevExcludePolygonsFilterRef.current = excludePolygonsFilter;
    prevCheckedScenarioIdsRef.current = checkedScenarioIds;

    if (!filtersLoaded) {
      return;
    }
    if (!isPopoutDashboard) {
      debouncedFilter();
    }
  }, [
    filtersLoaded,
    debouncedFilter,
    excludePolygonsFilter,
    isPopoutDashboard,
    polygonFilter,
    propertiesFilter,
    wellListFilter,
    checkedScenarioIds
  ]);

  // Turning the map view off, or back on, is a filter change like any other
  // and has to run one. Nothing else will: the polygon on file does not move,
  // so the main effect above sees no change, and the extent-sync effects
  // deliberately ignore this flag. The run counts as the user's, because it is
  // — the same as removing any other node from the filter tree.
  useEffect(() => {
    if (mapViewFilterDisabled === previousMapViewFilterDisabledRef.current) {
      return;
    }
    previousMapViewFilterDisabledRef.current = mapViewFilterDisabled;
    // An earlier pan may have armed the extent-sync flag for a polygon update
    // that the suppression then cancelled. Nothing is left to consume it, and
    // leaving it armed would cost the next real user polygon change its zoom.
    extentSyncRef.current = false;
    zoomEligibleRef.current = true;
    if (entityKind === EntityKind.Well) {
      useWellListOpenStore.getState().setPending(undefined);
    }
    if (mapViewFilterDisabled) {
      // Whatever is on file now is what the suppression freezes. Kept so the
      // restate below can tell it apart from a polygon that arrives later.
      frozenPolygonRef.current = latestPolygonFilterRef.current;
    }
    // The viewport is feeding the filter again. It stopped feeding it when the
    // node came out, so the polygon on file is wherever the camera was then,
    // and the pans since are not in it. Say where the camera is now before the
    // run below reads it, or the filter goes out against a view the user left
    // behind. Only the frozen polygon is restated: a polygon that arrived while
    // the map view was off replaced it deliberately, and one the user drew says
    // which wells to keep rather than where to look.
    if (
      !mapViewFilterDisabled &&
      latestPolygonFilterRef.current === frozenPolygonRef.current &&
      !latestPolygonFilterRef.current?.source &&
      mapExtent.coordinates.length
    ) {
      dispatch(
        entityKind === EntityKind.Facility
          ? filterFacilityFromMapExtent(mapExtent)
          : filterWellsFromMapExtent(mapExtent)
      );
    }
    if (!filtersLoaded || isPopoutDashboard) {
      return;
    }
    debouncedFilter();
  }, [
    mapViewFilterDisabled,
    filtersLoaded,
    isPopoutDashboard,
    debouncedFilter,
    dispatch,
    entityKind,
    mapExtent
  ]);

  // Puts the map view back after the run that left it out. The state the two
  // runs see is identical apart from the flag, so the extent path cannot do
  // this — a re-dispatch of the extent lands on the reducer's unchanged-
  // coordinates no-op.
  //
  // The cue is the finished run itself: the filter thunk raises this flag as
  // its last act, so a run that failed, was cancelled, or never got as far as
  // a request owes nothing. Waiting for a still camera then covers both
  // endings with one rule — after a zoom to the list the follow-up reads the
  // viewport the flight ended on, and with no zoom the camera never moved so
  // it runs at once. Clearing the flag before the call is what keeps it to
  // one run: the camera moving and settling again finds nothing owed.
  useEffect(() => {
    if (isPopoutDashboard) {
      return;
    }
    if (isMapMoving) {
      // The camera started again before the scheduled run fired — mapbox can
      // stop a flight and start another one — so the viewport that run would
      // have read is not the one the camera will end on. Take the run back and
      // owe it again; the next stop schedules it afresh, so it still happens
      // exactly once. A user filter change that landed in the same window is
      // deferred with it rather than lost: the debt runs the filter, and the
      // change is still marked as the user's when it does.
      //
      // React 18 batches the write below with the rest of this pass, so the
      // effect re-entry it causes sees both the cleared pending run and the
      // re-armed debt. Without that batching the pair could be observed half
      // applied and schedule a second run.
      //
      // Each hand-off between the debt and the scheduled run raises the new
      // flag before clearing the old one. Popouts mirror every write as its own
      // message, and clearing first would hand them an all-clear in between.
      if (followUpPendingRef.current) {
        followUpPendingRef.current = false;
        setFollowUpOwed(true);
        debouncedFilter.cancel();
      }
      return;
    }
    if (!mapViewFollowUpOwed) {
      return;
    }
    if (mapViewFilterDisabled) {
      // The map view is off for this list, so there is no node to put back.
      // The cancellation above takes back whatever the window was carrying,
      // and a filter change of the user's may have been in it — turning this
      // node off is itself one. Nothing else would start it again: the polygon
      // it would have changed is held still while the map view is off.
      if (zoomEligibleRef.current) {
        debouncedFilter();
      }
      setFollowUpOwed(false);
      return;
    }
    followUpPendingRef.current = true;
    debouncedFilter();
    setFollowUpOwed(false);
  }, [
    setFollowUpOwed,
    mapViewFollowUpOwed,
    isMapMoving,
    mapViewFilterDisabled,
    isPopoutDashboard,
    debouncedFilter
  ]);
}
